import {toast} from "sonner";
import {CheckCircleIcon, XCircleIcon} from "@heroicons/react/24/outline";
import {XMarkIcon} from "@heroicons/react/20/solid";

type Variant = "success" | "error";

const styles: Record<Variant, { bar: string; icon: string }> = {
    success: {bar: "bg-green-500", icon: "text-green-500"},
    error: {bar: "bg-red-500", icon: "text-red-500"},
};

function Card({
                  t, variant, title, body,
              }: { t: string | number; variant: Variant; title: string; body?: string }) {
    const Icon = variant === "success" ? CheckCircleIcon : XCircleIcon;

    return (
        <div
            className="pointer-events-auto w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10">
            <div className={`h-1 ${styles[variant].bar}`}/>
            <div className="p-4">
                <div className="flex items-start">
                    <div className="shrink-0">
                        <Icon aria-hidden className={`size-6 ${styles[variant].icon}`}/>
                    </div>

                    {/* ⬇️ was w-0 flex-1 -> use min-w-0 flex-1 so text can wrap */}
                    <div className="ml-3 min-w-0 flex-1 pt-0.5">
                        <p className="text-sm font-medium text-blue-900 dark:text-white">{title}</p>
                        {body && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{body}</p>}
                    </div>

                    <button
                        onClick={() => toast.dismiss(t)}
                        className="ml-4 shrink-0 rounded-md p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-2 focus:outline-offset-2 focus:outline-blue-800"
                    >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="size-5"/>
                    </button>
                </div>
            </div>
        </div>
    );
}

function show(variant: Variant, body: string, opts?: { title?: string; duration?: number }) {
    const title = opts?.title ?? (variant === "success" ? "Success" : "Something went wrong");
    const duration = opts?.duration ?? (variant === "success" ? 3500 : 5000);

    toast.custom((t) => <Card t={t} variant={variant} title={title} body={body}/>, {
        position: "top-right",
        duration,
        // ⬇️ Force a sane width so it can't collapse
        style: {width: "24rem", maxWidth: "24rem", padding: 0, background: "transparent", boxShadow: "none"},
        className: "p-0 bg-transparent shadow-none",
    });
}

export const notify = {
    info: (body: string, opts?: { title?: string; duration?: number }) => show("success", body, opts),
    success: (body: string, opts?: { title?: string; duration?: number }) => show("success", body, opts),
    error: (body: string, opts?: { title?: string; duration?: number }) => show("error", body, opts),
};
